import { FC, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { EyeVisible } from '@factorialco/f0-react/icons/app'
import { ProjectsAssignedDataCollectionDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import PendingTasksSidePanel from 'modules/project_management/components/PendingTasksSidePanel'

interface PendingTasksSidePanelOptions {
  projectId?: number
  projectName: string
}

interface ProjectsAssignedDatacollectionProps {
  employeeId: number
}

const ProjectsAssignedDatacollection: FC<ProjectsAssignedDatacollectionProps> = ({
  employeeId,
}) => {
  const { pathname } = useLocation()
  const { navigation } = useNavigation()

  const [showPendingTasksSidePanel, setShowPendingTasksSidePanel] = useState<boolean>(false)
  const [sidePanelOptions, setSidePanelOptions] = useState<PendingTasksSidePanelOptions>({
    projectId: undefined,
    projectName: '',
  })

  const isContributorsView = pathname.includes('contributors')

  const source = useGraphqlDataCollectionSource({
    document: ProjectsAssignedDataCollectionDocument,
    mapVariables: ({ search, sortings }) => ({
      employeeId,
      nameOrCode: search,
      sortOrder: sortings?.[0],
    }),
    mapData: (data) => data.projectManagement.imputableProjectsConnection.nodes,
    mapPagination: (data) => ({
      total: data.projectManagement.imputableProjectsConnection.totalCount ?? 0,
    }),
    totalItemSummary: (totals) => {
      return i18n.tp('project_management.table.project_assigned.total_items', { count: totals })
    },
    itemUrl: isContributorsView ? ({ id }) => navigation.projects.id.fullPath({ id }) : undefined,
    itemActions: ({ id, name, projectTasksConnection }) => {
      const totalPendingTasks = projectTasksConnection?.totalCount ?? 0

      if (totalPendingTasks === 0) {
        return []
      }

      return [
        {
          label: i18n.t('project_management.table.projects_list.table_options.pending_tasks'),
          icon: EyeVisible,
          onClick: () => {
            setShowPendingTasksSidePanel(true)
            setSidePanelOptions({ projectId: id, projectName: name })
            track(
              trackingEvents.projectManagement.flexibleTracking.overview
                .openOneProjectPendingTasksSidePanel
            )
          },
        },
      ]
    },
    search: {
      enabled: true,
    },
    sortings: {
      name: {
        label: i18n.t('project_management.table.projects_list.headers.name'),
      },
      code: {
        label: i18n.t('project_management.table.projects_list.headers.code'),
      },
      start_date: {
        label: i18n.t('project_management.table.projects_list.headers.startDate'),
      },
      due_date: {
        label: i18n.t('project_management.table.projects_list.headers.dueDate'),
      },
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('project_management.table.projects_list.headers.name'),
                  render: ({ name }) => ({
                    type: 'text',
                    value: name,
                  }),
                  sorting: 'name' as const,
                },
                {
                  label: i18n.t('project_management.table.projects_list.headers.code'),
                  render: ({ code }) => ({
                    type: 'text',
                    value: code ?? undefined,
                  }),
                  sorting: 'code' as const,
                },
                {
                  label: i18n.t('project_management.table.projects_list.headers.startDate'),
                  render: ({ startDate }) => ({
                    type: 'date',
                    value: startDate ? new Date(startDate) : undefined,
                  }),
                  sorting: 'start_date' as const,
                },
                {
                  label: i18n.t('project_management.table.projects_list.headers.dueDate'),
                  render: ({ dueDate }) => ({
                    type: 'date',
                    value: dueDate ? new Date(dueDate) : undefined,
                  }),
                  sorting: 'due_date' as const,
                },
                {
                  label: i18n.t(
                    'project_management.my_projects.overview.insight_cards.pending_tasks'
                  ),
                  render: ({ projectTasksConnection }) => {
                    const totalPendingTasks = projectTasksConnection?.totalCount ?? 0

                    return {
                      type: 'number',
                      value: totalPendingTasks,
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
      <PendingTasksSidePanel
        assigneeEmployeeId={employeeId}
        onClose={() => setShowPendingTasksSidePanel(false)}
        projectId={sidePanelOptions.projectId}
        projectName={sidePanelOptions.projectName}
        showPendingTasksSidePanel={showPendingTasksSidePanel}
      />
    </>
  )
}

export default ProjectsAssignedDatacollection
